/**
 * Hull Renderer Utility
 *
 * Renders convex hull tracking boundaries for annotated body regions.
 * Ported from the working PoC in frontend-annotation/js/region-renderer.js
 *
 * Features:
 * - Multiple region support with individual colors
 * - Configurable opacity and visibility
 * - Smooth polygon rendering with fill and stroke
 * - Frame-by-frame hull tracking
 */

export interface HullData {
  convex_hulls: Record<string, number[][]>;  // frame -> polygon points (backend uses string keys)
  color?: string;
  label?: string;
}

export interface HullSmoothingConfig {
  enabled: boolean;
  window_size: number;
  method: string;
}

export interface RegionHullData {
  annotation_id: string;
  hulls: Record<string, number[][]>;
  smoothing?: HullSmoothingConfig;
}

/**
 * Hull Renderer Class
 *
 * Manages and renders convex hulls for multiple tracked regions
 */
export class HullRenderer {
  private regions: Map<string, HullData> = new Map();
  private opacity: number = 0.6;
  private visible: boolean = true;

  constructor() {}

  /**
   * Add a region with its hull data
   *
   * @param id - Unique identifier for the region (annotation ID)
   * @param hullData - Hull data including convex_hulls and optional color
   */
  addRegion(id: string, hullData: HullData): void {
    this.regions.set(id, hullData);
  }

  /**
   * Remove a region by ID
   */
  removeRegion(id: string): void {
    this.regions.delete(id);
  }

  /**
   * Update an existing region's hull data
   */
  updateRegion(id: string, hullData: Partial<HullData>): void {
    const existing = this.regions.get(id);
    if (existing) {
      this.regions.set(id, { ...existing, ...hullData });
    }
  }

  /**
   * Clear all regions
   */
  clearAllRegions(): void {
    this.regions.clear();
  }

  /**
   * Set global opacity for all hull fills
   *
   * @param opacity - Value between 0 and 1
   */
  setOpacity(opacity: number): void {
    this.opacity = Math.max(0, Math.min(1, opacity));
  }

  /**
   * Show or hide all hulls
   */
  setVisible(visible: boolean): void {
    this.visible = visible;
  }

  /**
   * Get current opacity value
   */
  getOpacity(): number {
    return this.opacity;
  }

  /**
   * Get current visibility state
   */
  isVisible(): boolean {
    return this.visible;
  }

  /**
   * Get number of loaded regions
   */
  getRegionCount(): number {
    return this.regions.size;
  }

  /**
   * Render all hulls for the current frame
   *
   * @param ctx - Canvas rendering context
   * @param frameNumber - Current frame number
   * @param videoWidth - Video width (for bounds checking)
   * @param videoHeight - Video height (for bounds checking)
   */
  renderFrame(
    ctx: CanvasRenderingContext2D,
    frameNumber: number,
    _videoWidth: number,
    _videoHeight: number
  ): void {
    if (!this.visible) return;

    // Save canvas state
    ctx.save();

    this.regions.forEach((hullData, _regionId) => {
      // Backend returns string keys, so convert frameNumber to string
      const hull = hullData.convex_hulls[String(frameNumber)];

      // Skip if no hull for this frame or hull is too small
      if (!hull || hull.length < 3) return;

      const color = hullData.color || '#3b82f6'; // Default to blue

      // Draw filled polygon
      this.drawFilledPolygon(ctx, hull, color, this.opacity * 0.5);

      // Draw outline for better visibility
      this.drawPolygonOutline(ctx, hull, color, this.opacity);
    });

    // Restore canvas state
    ctx.restore();
  }

  /**
   * Draw a filled polygon
   */
  private drawFilledPolygon(
    ctx: CanvasRenderingContext2D,
    vertices: number[][],
    color: string,
    alpha: number
  ): void {
    if (vertices.length < 3) return;

    ctx.fillStyle = this.hexToRgba(color, alpha);
    ctx.beginPath();
    ctx.moveTo(vertices[0][0], vertices[0][1]);

    for (let i = 1; i < vertices.length; i++) {
      ctx.lineTo(vertices[i][0], vertices[i][1]);
    }

    ctx.closePath();
    ctx.fill();
  }

  /**
   * Draw polygon outline
   */
  private drawPolygonOutline(
    ctx: CanvasRenderingContext2D,
    vertices: number[][],
    color: string,
    alpha: number
  ): void {
    if (vertices.length < 3) return;

    ctx.strokeStyle = this.hexToRgba(color, alpha);
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(vertices[0][0], vertices[0][1]);

    for (let i = 1; i < vertices.length; i++) {
      ctx.lineTo(vertices[i][0], vertices[i][1]);
    }

    ctx.closePath();
    ctx.stroke();
  }

  /**
   * Convert hex color to rgba with alpha
   *
   * Supports multiple color formats:
   * - Hex: #FF0000
   * - RGB: rgb(255, 0, 0)
   * - RGBA: rgba(255, 0, 0, 0.5)
   */
  private hexToRgba(color: string, alpha: number): string {
    // Handle rgba format
    if (color.startsWith('rgba')) {
      // Replace existing alpha with new alpha
      return color.replace(/[\d.]+\)$/g, `${alpha})`);
    }

    // Handle rgb format
    if (color.startsWith('rgb')) {
      const match = color.match(/\d+/g);
      if (match && match.length >= 3) {
        return `rgba(${match[0]}, ${match[1]}, ${match[2]}, ${alpha})`;
      }
    }

    // Handle hex format (#FF0000 or #F00)
    if (color.startsWith('#')) {
      let hex = color.slice(1);

      // Expand shorthand hex (#F00 -> #FF0000)
      if (hex.length === 3) {
        hex = hex.split('').map(c => c + c).join('');
      }

      const r = parseInt(hex.slice(0, 2), 16);
      const g = parseInt(hex.slice(2, 4), 16);
      const b = parseInt(hex.slice(4, 6), 16);
      return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    }

    // Fallback to semi-transparent blue
    return `rgba(59, 130, 246, ${alpha})`;
  }

  /**
   * Get statistics for debugging/monitoring
   */
  getStats(): {
    totalRegions: number;
    visibleRegions: number;
    opacity: number;
    visible: boolean;
  } {
    return {
      totalRegions: this.regions.size,
      visibleRegions: this.visible ? this.regions.size : 0,
      opacity: this.opacity,
      visible: this.visible,
    };
  }

  /**
   * Get region-specific stats
   */
  getRegionStats(regionId: string): {
    frameCount: number;
    avgVertices: number;
    color: string;
  } | null {
    const region = this.regions.get(regionId);
    if (!region) return null;

    const frameCount = Object.keys(region.convex_hulls).length;
    const avgVertices = frameCount > 0
      ? Object.values(region.convex_hulls).reduce((sum, hull) => sum + hull.length, 0) / frameCount
      : 0;

    return {
      frameCount,
      avgVertices: parseFloat(avgVertices.toFixed(1)),
      color: region.color || '#3b82f6',
    };
  }
}
